import React, { useState, useRef } from 'react';
import ReactTable from 'react-table';
import { observer, inject } from 'mobx-react';
import { debounce } from 'lodash';
import classnames from 'classnames';
import useKeyboardEvent from '../../hooks/useKeyboardEvent.js';
import columns from './CharitiesTableColumns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTimes,
  faCaretUp,
  faCaretDown,
} from '@fortawesome/free-solid-svg-icons';

const CharitiesTable = ({ charitiesStore, onGotoCharity }) => {
  const [searchTerm, setSearchTerm] = useState(charitiesStore.searchString);
  const [mobileSortActive, setMobileSortActive] = useState(false);
  const [mobileSort, setMobileSort] = useState({
    id: 'name',
    desc: false,
  });

  const tableColumns = columns(charitiesStore.filterRows);

  let tableRef = useRef(null);
  let searchInputRef = useRef(null);

  const debouncedSetSearchTerm = debounce(setSearchTerm, 1000);

  useKeyboardEvent('Escape', () => {
    clearSearch();
  });

  const clearSearch = () => {
    setSearchTerm('');
  };

  const toggleMobileSort = (isOpen = true) => {
    if (isOpen) {
      setMobileSortActive(true);
      return;
    }
    setMobileSortActive(false);
  };

  return (
    <div className="rt-container">
      <div className="action-bar flex-row">
        <div className="responsive-row">
          <div className={classnames('search-input translate-x-2.5')}>
            <div className="input-container">
              <div tabIndex="0" className="action-button-search">
                <FontAwesomeIcon
                  icon={faSearch}
                  className={classnames('', {
                    orange: searchTerm !== '',
                  })}
                />
              </div>
              <input
                tabIndex={0}
                className="input"
                placeholder="Search Charities&#8230;"
                ref={searchInputRef}
                onChange={(e) => debouncedSetSearchTerm(e.target.value)}
                defaultValue={charitiesStore.searchString}
              />
              <div
                alt="clear all"
                className="action-button close-button flex-column"
                onClick={clearSearch}
              >
                <FontAwesomeIcon icon={faTimes} />
              </div>
            </div>
          </div>
        </div>
        <div className="responsive-row flex-expand">
          <div className="flex-row flex-expand is-hidden-tablet"></div>
          <div className="flex-row">
            <div
              className={classnames('sort-action flex-column is-hidden-tablet')}
              onClick={
                mobileSortActive
                  ? () =>
                      setMobileSort({ ...mobileSort, desc: !mobileSort.desc })
                  : () => {
                      setMobileSort({ ...mobileSort, desc: !mobileSort.desc });
                      toggleMobileSort(true);
                    }
              }
            >
              <FontAwesomeIcon
                icon={faCaretUp}
                className={classnames('flex items-end translate-y-[3px]', {
                  orange: mobileSort.desc,
                })}
              />
              <FontAwesomeIcon
                icon={faCaretDown}
                className={classnames('flex items-start translate-y-[-3px]', {
                  orange: !mobileSort.desc,
                })}
              />
            </div>
          </div>
        </div>
      </div>
      <ReactTable
        filterable
        className="ledger-table"
        resizable={false}
        keyField={'id'}
        ref={tableRef}
        columns={tableColumns}
        data={charitiesStore.charities}
        page={charitiesStore.page}
        pageSize={charitiesStore.pageSize}
        pages={Math.ceil(charitiesStore.totalCount / charitiesStore.pageSize)}
        minRows={0}
        getTdProps={onGotoCharity}
        filtered={[{ id: 'charity', value: searchTerm }]}
        defaultPageSize={charitiesStore.pageSize}
        showPaginationBottom={true}
        onPageChange={(page) => {
          charitiesStore.setPage(page);
          charitiesStore.getMoreCharities();
        }}
        {...(mobileSortActive > 0 && { sorted: [mobileSort] })}
      />
    </div>
  );
};

export default inject('charitiesStore')(observer(CharitiesTable));
